import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Pager from "@/components/Pager";
import { MessageSquare, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const PAGE_SIZE = 20;

export default function SmsOutbox() {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const load = async () => {
    const { data } = await api.get("/admin/sms", {
      params: { kind: kind || undefined, q: debouncedSearch || undefined, page, page_size: PAGE_SIZE },
    });
    setRows(data.items); setTotal(data.total);
  };
  useEffect(() => { load(); }, [kind, debouncedSearch, page]);

  return (
    <div>
      <PageHeader
        testId="sms-header"
        title="SMS Outbox"
        subtitle="Audit trail of outbound receipts, OTPs and notices"
        actions={
          <Button data-testid="sms-refresh-btn" onClick={load} variant="ghost" className="h-9 rounded-none border border-nhp-border">
            <RefreshCw className="h-4 w-4 mr-1" /> REFRESH
          </Button>
        }
      />
      <div className="p-4 md:p-8">
        <div className="mb-4 relative max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-nhp-muted" />
          <Input
            data-testid="sms-search"
            placeholder="Search by phone or message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-nhp-surface border-nhp-border text-nhp-text rounded-none h-10 font-mono"
          />
        </div>
        <div className="mb-4 flex gap-2 overflow-x-auto font-mono text-xs uppercase tracking-[0.2em]">
          {["", "receipt", "otp", "notice", "other"].map((k) => (
            <button key={k || "all"} data-testid={`sms-filter-${k || "all"}`}
                    onClick={() => { setKind(k); setPage(1); }}
                    className={`px-3 h-8 shrink-0 border ${kind === k ? "border-nhp-primary text-nhp-primary" : "border-nhp-border text-nhp-muted"}`}>
              {k || "all"}
            </button>
          ))}
        </div>
        <div className="border border-nhp-border bg-nhp-surface divide-y divide-nhp-border">
          {rows.length === 0 && <div className="p-10 text-center text-sm text-nhp-muted">No outbound SMS yet.</div>}
          {rows.map((r) => (
            <div key={r.id} data-testid={`sms-row-${r.id}`} className="px-4 md:px-5 py-3 flex flex-col gap-1.5 md:grid md:grid-cols-6 md:items-center md:gap-4 text-sm">
              <div className="flex items-center justify-between md:contents">
                <div className="font-mono">{r.phone}</div>
                <div className="font-mono text-xs text-nhp-muted md:col-span-1 md:order-first">{new Date(r.created_at).toLocaleTimeString()}</div>
              </div>
              <div className="flex items-center gap-2 md:col-span-3">
                <MessageSquare className="h-3.5 w-3.5 text-nhp-muted shrink-0" />
                <span className="text-xs truncate">{r.body}</span>
              </div>
              <div className="flex items-center gap-2 md:col-span-1 md:justify-end">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-nhp-muted">{r.kind}</span>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] ${
                  r.status === "sent" ? "text-nhp-primary" : r.status === "failed" ? "text-nhp-critical" : "text-nhp-muted"
                }`}>{r.status}</span>
              </div>
            </div>
          ))}
          <Pager page={page} total={total} pageSize={PAGE_SIZE} itemLabel="messages" onChange={setPage} testId="sms" />
        </div>
      </div>
    </div>
  );
}
